import { z } from "zod";
import { GROUP_SUSPENDED_CODE } from "./admin-group-lifecycle.js";
import { FUND_FROZEN_CODE } from "./cooling-freeze-anomaly.js";
import {
  BY_WEEKDAY_DUPLICATE_MESSAGE,
  BY_WEEKDAY_ONLY_WEEKLY_MESSAGE,
  COLLECTION_FREQUENCY,
  DEFAULT_RECURRENCE_INTERVAL,
  INTERVAL_INVALID_MESSAGE,
  RECURRENCE_INTERVAL_MAX,
  WEEKDAY_MAX,
  WEEKDAY_MIN,
  collectionFrequencySchema,
  scheduleStatusSchema,
} from "./collection-schedules.js";

/**
 * Shared DTO cho Admin Collections Oversight (US-AE4.F5-0091 · @UC9142) — giám sát đợt thu xuyên
 * quỹ/nhóm (READ-ONLY). Một nguồn sự thật cho api (S01 — query + redaction theo vai) và admin web
 * (S03 — render), tránh drift shape ở 2 nơi (DRY). Mirror style admin-user-directory.ts.
 *
 * BẢO MẬT (invariants):
 * - B1: `amountPerMemberMinor` = BigInt minor-units serialize STRING (không float/number JS). NULLABLE
 *   + OPTIONAL — null khi redact theo vai (SUPPORT/READ_ONLY) HOẶC khi schema null (adhoc/settlement).
 * - C3: schema `.strict()` — KHÔNG khai báo mức tiền thành viên (MemberDue) / secret; field lạ bị
 *   reject tại biên.
 * - Cursor opaque string — đối xứng common/pagination/cursor.
 * - status enum = đúng schema `Collection.status` (open|closed); KHÔNG bịa `running` (MOCK cũ).
 *
 * Envelope khớp quy ước list repo: `{ data, pageInfo: { hasNext, nextCursor } }`.
 */

/** Max page-size. Default (20) do backend áp khi vắng (mirror admin-user-directory). */
export const ADMIN_COLLECTION_MAX_LIMIT = 50;

/** Trạng thái đợt thu — đúng schema `Collection.status` (open|closed). KHÔNG dùng MOCK `running`. */
export const adminCollectionStatusSchema = z.enum(["open", "closed"]);

export type AdminCollectionStatus = z.infer<typeof adminCollectionStatusSchema>;

/**
 * Loại đợt thu — đúng schema `Collection.kind`.
 * US-E16.F5-0186 (@UC9245–@UC9247): thêm thu ĐẶC THÙ `penalty` (phạt) / `usage` (theo buổi/điểm danh)
 * / `by_area` (theo m²). Thiếu 3 giá trị này ⇒ `map-collection.ts` parse thất bại ⇒ oversight 500 khi
 * quỹ có đợt thu đặc thù (dead-flow). Mở rộng ADDITIVE — kind cũ KHÔNG regress.
 */
export const adminCollectionKindSchema = z.enum([
  "adhoc",
  "periodic",
  "settlement",
  "penalty",
  "usage",
  "by_area",
  // FUND-DEBT-REPAY-PATH — đợt hệ thống "nợ quỹ" (`COLLECTION_KIND.FUND_DEBT`). Thiếu ⇒ `map-collection.ts` parse hỏng ⇒ oversight 500.
  "fund_debt",
]);

export type AdminCollectionKind = z.infer<typeof adminCollectionKindSchema>;

/**
 * US-E16.F1-0182 (@UC9231–@UC9234) — chế độ thu (đúng schema `Collection.amount_mode`). Oversight
 * hiện mode để admin biết đợt thu theo cách nào (equal/per_member/tiered/target_split/open_amount).
 */
export const adminCollectionAmountModeSchema = z.enum([
  "equal",
  "per_member",
  "tiered",
  "target_split",
  "open_amount",
]);

export type AdminCollectionAmountMode = z.infer<typeof adminCollectionAmountModeSchema>;

/**
 * `amountPerMemberMinor` = BigInt minor-units → string (B1, no float) HOẶC null (redact theo vai /
 * schema null). regex chỉ nhận chuỗi số nguyên (dấu âm cho phép) — reject float `"123.45"`.
 */
const amountPerMemberMinorString = z
  .string()
  .regex(/^-?\d+$/, "amountPerMemberMinor phải là số nguyên minor units (không float)")
  .nullable()
  .optional();

/** `YYYY-MM-DD` (date-only). Dùng cho filter khoảng ngày tạo (US-E16.F6-0187 · @UC9250). */
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** True nếu chuỗi `YYYY-MM-DD` là NGÀY THẬT (chống "2026-13-45" lọt regex → Invalid Date → 500). */
function isRealDate(value: string): boolean {
  const ts = Date.parse(`${value}T00:00:00.000Z`);
  if (Number.isNaN(ts)) return false;
  return new Date(ts).toISOString().slice(0, 10) === value;
}

/**
 * Query list (AC2 filter + cursor): `fundId?` (uuid), `status?` (open|closed), `kind?`
 * (adhoc|periodic|settlement), cursor opaque optional, limit ∈ [1,50] optional. Vắng filter ⇒ list
 * toàn bộ theo cursor. Validate tại biên (Zod) — single source api ↔ admin.
 */
export const adminCollectionQuerySchema = z.object({
  fundId: z.string().uuid().optional(),
  /**
   * US-AE4.F7-0909-S09 (@UC9642): lọc theo NHÓM (qua quỹ của nhóm). Màn Đợt thu chọn theo nhóm vì mọi quỹ đều mang
   * tên "Quỹ chung" — chọn theo tên quỹ vô nghĩa. AND với các bộ lọc khác. Optional ⇒ không đổi hành vi cũ.
   */
  groupId: z.string().uuid().optional(),
  /**
   * US-AE4.F7-1001 (@UC9642): chỉ các đợt (kỳ) do MỘT lịch định kỳ sinh ra — dựng tab "Các kỳ" của trang đợt thu.
   * AND với các bộ lọc khác. Optional ⇒ không đổi hành vi cũ.
   */
  scheduleId: z.string().uuid().optional(),
  status: adminCollectionStatusSchema.optional(),
  kind: adminCollectionKindSchema.optional(),
  /** US-AE4.F7-1008 (@UC9642): lọc theo chế độ thu. AND với các bộ lọc khác. Giá trị ngoài enum ⇒ 400 ở biên. */
  amountMode: adminCollectionAmountModeSchema.optional(),
  /**
   * US-AE5.F7-0157 (@UC9166): full-text search bỏ dấu trên `Collection.title` (server fold qua
   * `public.expenses_search_norm`). Optional (default undefined ⇒ KHÔNG đổi hành vi list cũ). ≤256
   * ký tự (validate biên chống payload dài). READ-ONLY.
   */
  q: z.string().max(256).optional(),
  /**
   * US-E16.F6-0187 (@UC9250): khoảng NGÀY TẠO đợt thu (`createdAt`), lọc SERVER-side (không client).
   * Định dạng `YYYY-MM-DD` (date-only, UTC). `dateFrom` inclusive từ 00:00; `dateTo` inclusive TRỌN
   * ngày (service cộng 1 ngày exclusive). Ngày rác ⇒ 400 tại biên (KHÔNG 500).
   */
  dateFrom: z.string().regex(ISO_DATE_RE).refine(isRealDate, "dateFrom không hợp lệ").optional(),
  dateTo: z.string().regex(ISO_DATE_RE).refine(isRealDate, "dateTo không hợp lệ").optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(ADMIN_COLLECTION_MAX_LIMIT).optional(),
});

export type AdminCollectionQuery = z.infer<typeof adminCollectionQuerySchema>;

/**
 * List item (AC1). `title`/`fundName`/`groupName`/`dueDate` NULLABLE+OPTIONAL (schema nullable /
 * join miss). `amountPerMemberMinor` string|null (BigInt / redact). KHÔNG bao giờ chứa mức tiền
 * thành viên / secret (`.strict()`).
 */
export const adminCollectionItemSchema = z
  .object({
    id: z.string(),
    title: z.string().nullable().optional(),
    fundId: z.string(),
    fundName: z.string().nullable().optional(),
    groupId: z.string(),
    groupName: z.string().nullable().optional(),
    kind: adminCollectionKindSchema,
    /** US-E16.F1-0182 (@UC9231–@UC9234): chế độ thu — NON-tiền, KHÔNG redact. Default 'equal'. */
    amountMode: adminCollectionAmountModeSchema.default("equal"),
    amountPerMemberMinor: amountPerMemberMinorString,
    currency: z.string(),
    dueDate: z.string().nullable().optional(),
    status: adminCollectionStatusSchema,
    createdAt: z.string(),
    /** US-AE4.F7-1001 (@UC9642): kỳ của đợt định kỳ (vd `2026-09`); đợt một lần ⇒ null. `.optional()` cho response cũ. */
    periodKey: z.string().nullable().optional(),
  })
  .strict();

export type AdminCollectionItem = z.infer<typeof adminCollectionItemSchema>;

/** Envelope khớp quy ước repo `{ data, pageInfo }` (cursor opaque, hasNext). */
export const adminCollectionResponseSchema = z.object({
  data: z.array(adminCollectionItemSchema),
  pageInfo: z.object({
    hasNext: z.boolean(),
    nextCursor: z.string().nullable(),
  }),
});

export type AdminCollectionResponse = z.infer<typeof adminCollectionResponseSchema>;

/**
 * Detail read-only (US-AE-IMP2-T11-S4 · @UC9154) — GET /admin/collections/:id. Mở rộng list item với
 * `periodKey` + aggregate trạng thái đợt thu (KHÔNG danh tính từng thành viên):
 * - `dueCountByStatus` = đếm MemberDue theo status (pending/paid/waived/…) — CHỈ số đếm, KHÔNG ai nợ.
 * - `collectedMinor`/`expectedMinor` = TỔNG minor (BigInt→string) redact theo vai (SUPPORT/READ_ONLY
 *   → null; SUPER_ADMIN/OPS → string). KHÔNG per-member amount.
 * `.strict()` reject field lạ / danh tính. READ-ONLY (KHÔNG write path).
 */
const dueStatusCountSchema = z
  .object({
    status: z.string(),
    count: z.number().int().min(0),
  })
  .strict();

export type AdminCollectionDueStatusCount = z.infer<typeof dueStatusCountSchema>;

const totalMinorString = z
  .string()
  .regex(/^-?\d+$/, "tổng phải là số nguyên minor units (không float)")
  .nullable()
  .optional();

/**
 * US-E23.F15-0347-S01 (@UC9540–@UC9542) — LUẬT LẶP sinh ra đợt thu, cho oversight.
 *
 * VÌ SAO CẦN: `{frequency, interval, byWeekday}` là thứ engine đọc để **sinh `member_dues`** (⇒ sinh
 * TIỀN). Trước task này `apps/admin` có **0 chỗ** đọc nó ⇒ OPS nhận khiếu nại *"sao tôi bị thu 2
 * tuần/lần?"* không trả lời được nếu không vào DB. Mặt giám sát duy nhất của hệ thống mà mù với luật
 * sinh tiền thì minh bạch chỉ còn là khẩu hiệu.
 *
 * INVARIANTS:
 *  - 🔴 **TÁI DÙNG `collectionFrequencySchema` + `scheduleStatusSchema`** (collection-schedules.ts) —
 *    KHÔNG khai enum thứ hai ở đây. Repo đã có tiền lệ drift 2 whitelist txType; enum tần suất là
 *    thứ quyết định `member_dues` sinh khi nào, lệch một bản là lệch tiền.
 *  - 🔴 KHÔNG có `'biweekly'`: "2 tuần/lần" = `{weekly, interval: 2}` (collection-schedules.ts:49-51).
 *  - Biên `interval ∈ [1..RECURRENCE_INTERVAL_MAX]` **khớp CHECK ở DB**
 *    (`collection_schedules_recurrence_interval_check`) — lệch app↔DB là bug chờ nổ.
 *  - `byWeekday` CHỈ có nghĩa với `weekly`; đi kèm tần suất khác ⇒ **reject**, KHÔNG im lặng bỏ qua
 *    (im lặng ⇒ admin đọc "Thứ Tư" trên màn mà engine không hề dùng thứ Tư).
 *  - **KHÔNG mang field tiền nào.** Chu kỳ là LUẬT vận hành ⇒ hiện cho MỌI vai admin, giống
 *    `amountMode`. Nhét tiền vào đây sẽ kéo theo nghĩa vụ redact và SUPPORT mất luôn khả năng đọc
 *    chu kỳ — redact nhầm thứ không phải tiền làm hỏng việc hỗ trợ.
 *  - `.strict()` chặn field lạ.
 */
export const adminCollectionRecurrenceSchema = z
  .object({
    scheduleId: z.string().uuid(),
    scheduleName: z.string(),
    scheduleStatus: scheduleStatusSchema,
    frequency: collectionFrequencySchema,
    interval: z
      .number()
      .int(INTERVAL_INVALID_MESSAGE)
      .min(DEFAULT_RECURRENCE_INTERVAL, INTERVAL_INVALID_MESSAGE)
      .max(RECURRENCE_INTERVAL_MAX, INTERVAL_INVALID_MESSAGE),
    byWeekday: z.array(z.number().int().min(WEEKDAY_MIN).max(WEEKDAY_MAX)).nullable(),
  })
  .strict()
  .superRefine((val, ctx) => {
    if (val.frequency !== COLLECTION_FREQUENCY.WEEKLY && val.byWeekday !== null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: BY_WEEKDAY_ONLY_WEEKLY_MESSAGE,
        path: ["byWeekday"],
      });
    }
    if (val.byWeekday && new Set(val.byWeekday).size !== val.byWeekday.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: BY_WEEKDAY_DUPLICATE_MESSAGE,
        path: ["byWeekday"],
      });
    }
  });

export type AdminCollectionRecurrence = z.infer<typeof adminCollectionRecurrenceSchema>;

/**
 * US-AE4.F7-1003 (@UC9636) — MỘT lần nhân viên đổi số tiền (đọc từ `audit_log`, không từ sổ giá: sổ giá không có người đổi
 * lẫn lý do, và cùng mốc bị THAY nên mất số cũ). Tiền = string minor | null theo vai (SUPPORT/READ_ONLY ⇒ null);
 * `actorEmail` là PII ⇒ null với vai không xem được PII. `reason` là ghi chú vận hành, mọi vai admin đọc được.
 */
export const adminAmountChangeItemSchema = z
  .object({
    at: z.string(),
    scope: z.enum(["this_collection", "from_period"]),
    /** Kỳ bắt đầu dùng số mới; `null` với `this_collection`. */
    effectiveFromPeriodKey: z.string().nullable(),
    amountBeforeMinor: z.string().nullable(),
    amountAfterMinor: z.string().nullable(),
    reason: z.string(),
    actorEmail: z.string().nullable(),
  })
  .strict();
export type AdminAmountChangeItem = z.infer<typeof adminAmountChangeItemSchema>;

/**
 * US-AE4.F7-1005: vì sao KHÔNG sửa hạn được (ngoài `actionBlock` và đợt đã đóng) — `not_adhoc` (không phải đợt một lần) ·
 * `no_due_date` (đợt không có hạn) · `overdue` (đã quá hạn, tính cả ân hạn của quỹ). `null` = sửa được.
 */
export const ADMIN_DUE_DATE_LOCKS = ["not_adhoc", "no_due_date", "overdue"] as const;

/**
 * US-AE4.F7-1011 (@UC9879): vì sao nút "Thêm người" ở tab Người tham gia mờ — `all_members` (đợt / lịch "toàn bộ nhóm": người vào
 * nhóm tự được thêm) · `not_supported` (không chia đều · quyết toán · gói chung) · `closed` (đợt một lần đã đóng) ·
 * `schedule_cancelled` (lịch đã huỷ — lịch tạm dừng vẫn thêm được, QĐ 07/10). `null` = thêm được — tính bằng CHÍNH vị-từ của lõi gắn đợt.
 */
export const ADMIN_ADD_PARTICIPANT_LOCKS = ["all_members", "not_supported", "closed", "schedule_cancelled"] as const;
export type AdminAddParticipantLock = (typeof ADMIN_ADD_PARTICIPANT_LOCKS)[number];
export type AdminDueDateLock = (typeof ADMIN_DUE_DATE_LOCKS)[number];

/** Một lần sửa hạn (đọc từ audit). `actorEmail` redact theo `canViewPii`; `reason` là ghi chú vận hành, không redact. */
export const adminDueDateChangeItemSchema = z
  .object({
    at: z.string(),
    dueDateBefore: z.string(),
    dueDateAfter: z.string(),
    reason: z.string(),
    actorEmail: z.string().nullable(),
  })
  .strict();
export type AdminDueDateChangeItem = z.infer<typeof adminDueDateChangeItemSchema>;

export const adminCollectionDetailSchema = adminCollectionItemSchema
  .extend({
    periodKey: z.string().nullable().optional(),
    /**
     * US-AE4.F7-1002 (@UC9636): vì sao nhân viên KHÔNG làm hộ được đợt này — nhóm đình chỉ / quỹ đóng băng (US-AE4.F7-1010).
     * `null` = làm hộ được. Màn dùng để KHOÁ nút kèm lý do (server vẫn 409 đúng mã). `.optional()` cho response cũ.
     */
    // US-AE4.F7-1010 (QĐ người dùng 06/10/2026): nhóm lưu trữ không còn chặn — chỉ đình chỉ · đóng băng (hằng của app).
    actionBlock: z.enum([GROUP_SUSPENDED_CODE, FUND_FROZEN_CODE]).nullable().optional(),
    /**
     * US-AE4.F7-1010: mốc nhóm lưu trữ (ISO) — nhóm lưu trữ làm hộ được MỌI thao tác TRỪ bật lại lịch thu (worker bỏ qua nhóm lưu
     * trữ) ⇒ màn khoá RIÊNG nút "Bật lại". Cùng khai báo với `groupArchivedAt` của danh sách lịch thu. `.optional()` cho response cũ.
     */
    groupArchivedAt: z.string().nullable().optional(),
    /** Đếm MemberDue theo status — aggregate, KHÔNG danh tính thành viên. */
    dueCountByStatus: z.array(dueStatusCountSchema),
    /** Tổng đã thu (minor) — redact theo vai (low → null). KHÔNG per-member. */
    collectedMinor: totalMinorString,
    /** Tổng kỳ vọng (minor) — redact theo vai (low → null). KHÔNG per-member. */
    expectedMinor: totalMinorString,
    /**
     * Luật lặp của lịch sinh ra đợt này.
     * 🔴 `null` là trạng thái HỢP LỆ, KHÔNG phải lỗi: đợt `adhoc`/`settlement` có `scheduleId = null`
     * (schema.prisma — `model Collection`). **Không được bịa `{weekly, interval:1}`** cho đợt không
     * định kỳ — đó là nói dối về luật sinh tiền.
     * `.optional()` để response cũ (trước task này) không vỡ ở client đang chạy.
     */
    recurrence: adminCollectionRecurrenceSchema.nullable().optional(),
    /**
     * US-AE4.F7-1003: kỳ KẾ SAU kỳ đã sinh gần nhất của lịch — nơi một lần "đổi số tiền" sẽ có hiệu lực — và giá đang hiệu
     * lực cho kỳ đó (giá gốc hoặc mốc trong sổ giá; redact theo vai). `null` với đợt không định kỳ, lịch đã huỷ, hoặc
     * không xác định được kỳ (thiếu ngày kết thúc). `.optional()` cho response cũ.
     */
    nextPeriodKey: z.string().nullable().optional(),
    nextPeriodAmountMinor: z.string().nullable().optional(),
    /** Lịch sử đổi số tiền (mới nhất trước, tối đa 20; đợt định kỳ ⇒ gộp mọi kỳ của cùng lịch). */
    amountChanges: z.array(adminAmountChangeItemSchema).optional(),
    /** US-AE4.F7-1005: lý do không sửa hạn được (`null` = được). `.optional()` cho response cũ. */
    dueDateLock: z.enum(ADMIN_DUE_DATE_LOCKS).nullable().optional(),
    /** US-AE4.F7-1005: lịch sử sửa hạn (mới nhất trước, tối đa 20). */
    dueDateChanges: z.array(adminDueDateChangeItemSchema).optional(),
    /** US-AE4.F7-1011: lý do không thêm người được (`null` = được). `.optional()` cho response cũ. */
    addParticipantLock: z.enum(ADMIN_ADD_PARTICIPANT_LOCKS).nullable().optional(),
    /**
     * US-AE4.F7-1011: đợt áp cho `all` (toàn bộ nhóm — người vào nhóm tự được thêm) hay `subset` (chọn người). Đợt một lần đọc
     * `appliesToAllMembers`; kỳ định kỳ đọc danh sách của LỊCH (rỗng = toàn bộ). Quyết toán và loại khác ⇒ `null`.
     */
    scope: z.enum(["all", "subset"]).nullable().optional(),
    /**
     * US-AE4.F7-1014 (QĐ người dùng 12/10/2026): các kỳ CHƯA được tạo của lịch sinh ra đợt này — mọi mốc từ ngày bắt đầu tới hôm
     * nay mà chưa có đợt (trừ kỳ nhóm bỏ qua, mốc hôm nay cron chưa chạy). KHÔNG khẳng định "máy lỗi": có thể lịch từng tạm dừng —
     * nhân viên tự chọn kỳ muốn tạo. `occurrenceDate` = ngày mốc 'YYYY-MM-DD' (giờ VN). Rỗng = không thiếu / đợt không định kỳ.
     * `.optional()` cho response cũ.
     */
    missingPeriods: z
      .array(z.object({ periodKey: z.string(), occurrenceDate: z.string() }).strict())
      .optional(),
  })
  .strict();

export type AdminCollectionDetail = z.infer<typeof adminCollectionDetailSchema>;
